import {Command} from "commander";

export * from "./runner";

import {spawn} from "node:child_process";
import {resolve, isAbsolute} from "node:path";
import {getVersion} from "../version.ts";
import {themeManager, type ThemeName, themes} from "../utils/console/themes.ts";
import type {ZodAxogenConfig} from "../config/types";
import {loadConfig} from "../config/loader.ts";
import {createGenerateCommand} from "./commands/generate.ts";
import {createThemeCommand} from "./commands/theme.ts";
import {buildDynamicCommands} from "./builder.ts";
import {logger} from "../utils/console/logger.ts";

/**
 * Creates and configures the main CLI application with all commands and options.
 * Sets up global options, pre-action hooks for configuration, and dynamic commands.
 * @returns Promise that resolves to the configured Command instance
 */
export async function createCLI(): Promise<Command> {
    const cli = new Command();

    cli.name("axogen")
        .description("TypeScript-native configuration and task management")
        .version(getVersion());

    // Global options
    cli.option("--verbose", "Enable verbose logging");
    cli.option("--quiet", "Suppress non-essential output");
    cli.option("--no-color", "Disable colored output");
    cli.option(
        "--theme <name>",
        `Color theme to use (${Object.keys(themes).join(", ")})`,
        process.env.AXOGEN_THEME || "doom-one" // Default theme
    );

    // Configure pretty printing based on global options early
    cli.hook("preAction", (thisCommand) => {
        const opts = thisCommand.optsWithGlobals();

        // Validate and set theme
        if (opts.theme && !(opts.theme in themes)) {
            console.error(
                `❌ Invalid theme "${opts.theme}". Available themes: ${Object.keys(themes).join(", ")}`
            );
            process.exit(1);
        }

        themeManager.setTheme(opts.theme);
        themeManager.colorOutput(
            !opts.noColor && !process.env.NO_COLOR && process.stdout.isTTY
        );
    });

    // Load config once
    let config: ZodAxogenConfig;
    try {
        config = await loadConfig();
    } catch (error) {
        // If config fails to load, still allow basic commands
        logger.warn(
            `Could not load config: ${error instanceof Error ? error.message : error}`
        );
        config = {};
    }

    // Add built-in commands (pass config to them)
    cli.addCommand(createGenerateCommand(config));
    cli.addCommand(createThemeCommand());

    // Add dynamic commands from config to the "run" command
    const runCommand = new Command("run").description(
        "Run custom commands defined in configuration"
    );

    if (config.commands && Object.keys(config.commands).length > 0) {
        buildDynamicCommands(runCommand, config);
    } else {
        // Add a default action to show available commands or a helpful message
        runCommand.action(() => {
            logger.info("No commands defined in config");
        });
    }

    cli.addCommand(runCommand);

    return cli;
}

/**
 * Execute a shell command and capture output.
 * Runs the command in a child process and returns stdout, stderr, and exit code.
 * @param command - Shell command to execute
 * @param options - Execution options including working directory and environment variables
 * @returns Promise resolving to command output and exit code
 */
export function executeCommand(
    command: string,
    options: {
        cwd?: string;
        env?: NodeJS.ProcessEnv;
    } = {}
): Promise<{stdout: string; stderr: string; exitCode: number}> {
    return new Promise((resolve, reject) => {
        const child = spawn(command, {
            shell: true,
            cwd: options.cwd || process.cwd(),
            env: {...process.env, ...options.env},
            stdio: ["pipe", "pipe", "pipe"],
        });

        let stdout = "";
        let stderr = "";

        child.stdout.on("data", (data) => {
            stdout += data.toString();
        });

        child.stderr.on("data", (data) => {
            stderr += data.toString();
        });

        child.on("close", (exitCode) => {
            resolve({stdout, stderr, exitCode: exitCode || 0});
        });

        child.on("error", (error) => {
            reject(error);
        });
    });
}

export interface LiveExecOptions {
    cwd?: string;
    env?: NodeJS.ProcessEnv;
    /** Prefix for live output lines */
    outputPrefix?: string;
    /** Whether this command needs interactive input (default: auto-detect) */
    interactive?: boolean;
}

export interface LiveExecResult {
    exitCode: number;
    /** Whether the command was terminated by user action */
    wasTerminated: boolean;
}

/**
 * Auto-detect if a command is likely to be interactive
 */
function isInteractiveCommand(command: string): boolean {
    const interactiveKeywords = [
        "dev",
        "serve",
        "start",
        "watch",
        "server",
        "daemon",
        "tail",
        "logs",
        "monitor",
        "repl",
        "shell",
        "bash",
        "zsh",
    ];

    return interactiveKeywords.some((keyword) =>
        command.toLowerCase().includes(keyword)
    );
}

/**
 * Execute a shell command with live input/output streaming.
 * Supports interactive commands and provides real-time output with proper signal handling.
 * Automatically detects interactive commands and enables stdin forwarding when appropriate.
 * @param command - Shell command to execute
 * @param options - Execution options including working directory, environment, and interactivity settings
 * @returns Promise resolving to exit code and termination status
 */
export function liveExec(
    command: string,
    options: LiveExecOptions = {}
): Promise<LiveExecResult> {
    const resolvedCwd =
        options.cwd && !isAbsolute(options.cwd)
            ? resolve(process.cwd(), options.cwd)
            : options.cwd;

    const {
        cwd = process.cwd(),
        env = {},
        outputPrefix = "OUTPUT",
        interactive = isInteractiveCommand(command),
    } = {...options, cwd: resolvedCwd};

    return new Promise((resolve, reject) => {
        let wasTerminated = false;
        let isCleaningUp = false;
        let originalSigintListeners: NodeJS.SignalsListener[] = [];

        logger.command(command);

        // Spawn child process in its own process group for proper termination
        const child = spawn(command, {
            shell: true,
            cwd,
            env: {...process.env, ...env},
            stdio: ["pipe", "pipe", "pipe"],
            detached: true, // Create new process group
        });

        // Setup signal handling - always intercept SIGINT in live mode
        originalSigintListeners = process.listeners(
            "SIGINT"
        ) as NodeJS.SignalsListener[];
        process.removeAllListeners("SIGINT");

        const sigintHandler = () => {
            if (isCleaningUp) return;
            wasTerminated = true;
            cleanup();
        };
        process.on("SIGINT", sigintHandler);

        // Pipe child stdout to parent stdout with optional prefix
        child.stdout.on("data", (data) => {
            const output = data.toString();
            if (outputPrefix) {
                const lines = output.split("\n");
                const prefixedOutput = lines
                    .map((line: any) =>
                        line
                            ? logger.format(
                                  `<secondary>[${outputPrefix}]</secondary> ${line}`
                              )
                            : line
                    )
                    .join("\n");
                process.stdout.write(prefixedOutput);
            } else {
                process.stdout.write(output);
            }
        });

        // Pipe child stderr to parent stderr with optional prefix
        child.stderr.on("data", (data) => {
            const output = data.toString();
            if (outputPrefix) {
                const lines = output.split("\n");
                const prefixedOutput = lines
                    .map((line: any) =>
                        line
                            ? logger.format(
                                  `<secondary>[${outputPrefix}]</secondary> ${line}`
                              )
                            : line
                    )
                    .join("\n");
                process.stderr.write(prefixedOutput);
            } else {
                process.stderr.write(output);
            }
        });

        // Setup stdin forwarding only for interactive commands
        let inputHandler: ((data: Buffer) => void) | null = null;
        if (interactive && process.stdin.isTTY) {
            inputHandler = (data: Buffer) => {
                if (child.stdin && !child.stdin.destroyed) {
                    child.stdin.write(data);
                }
            };
            process.stdin.on("data", inputHandler);
        }

        // Cleanup function
        const cleanup = () => {
            if (isCleaningUp) return;
            isCleaningUp = true;

            // Kill child process and its entire process group
            if (child.pid && !child.killed) {
                try {
                    // Kill the entire process group
                    process.kill(-child.pid, "SIGTERM");

                    // If it doesn't die quickly, force kill it
                    setTimeout(() => {
                        if (!child.killed) {
                            try {
                                process.kill(-child.pid!, "SIGKILL");
                            } catch (e) {
                                // Process already dead, ignore
                            }
                        }
                    }, 1000);
                } catch (e) {
                    // Process already dead or not accessible, ignore
                }
            }

            // Clean up stdin listener
            if (inputHandler) {
                process.stdin.removeListener("data", inputHandler);
            }

            // Close child stdin if still open
            if (child.stdin && !child.stdin.destroyed) {
                child.stdin.end();
            }

            // Restore original SIGINT listeners
            process.removeAllListeners("SIGINT");
            originalSigintListeners.forEach((listener) => {
                process.on("SIGINT", listener);
            });
        };

        child.on("close", (exitCode) => {
            if (isCleaningUp) return;

            cleanup();

            if (wasTerminated) {
                logger.logF(`Stopped <subtle>${command}</subtle>`);
            } else {
                if (exitCode === 0) {
                    logger.success(`Completed: ${command}`);
                } else {
                    logger.error(
                        `Failed: ${command} <subtle>(${exitCode})</subtle>`
                    );
                }
            }

            resolve({
                exitCode: exitCode || 0,
                wasTerminated,
            });
        });

        child.on("error", (error) => {
            cleanup();
            logger.error(`Error: ${command}`);
            reject(error);
        });

        // Handle child process being killed
        child.on("exit", (_code, signal) => {
            if (signal && signal !== "SIGTERM" && signal !== "SIGKILL") {
                wasTerminated = true;
            }
        });

        // Handle uncaught exceptions and process exits
        const processExitHandler = () => {
            cleanup();
        };

        process.once("exit", processExitHandler);
        process.once("SIGTERM", processExitHandler);
        process.once("uncaughtException", processExitHandler);
    });
}

export const exec = executeCommand;
